export const WEFAT_IMAGE_PREVIEW_API_NAMES = Object.freeze(['previewImage']);
export class WxImagePreviewRuntimeError extends Error {
    api = 'previewImage';
    code;
    errCode;
    errMsg;
    remediation;
    constructor(code, reason, remediation, cause) {
        const errMsg = `previewImage:fail ${reason}`;
        super(`${code} ${errMsg}`);
        this.name = 'WxImagePreviewRuntimeError';
        this.code = code;
        this.errCode = code;
        this.errMsg = errMsg;
        this.remediation = remediation;
        if (cause !== undefined)
            Object.defineProperty(this, 'cause', { value: cause });
    }
}
const CALLBACK_KEYS = new Set(['success', 'fail', 'complete']);
const OPTION_KEYS = new Set([...CALLBACK_KEYS, 'current', 'urls']);
const MAX_IMAGE_COUNT = 100;
const MAX_URL_LENGTH = 16_384;
const activeViewers = new WeakMap();
function isPlainRecord(value) {
    if (value === null || typeof value !== 'object' || Array.isArray(value))
        return false;
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}
function optionError(reason) {
    return new WxImagePreviewRuntimeError('WF6231', reason, 'Pass a non-empty urls array of safe image URLs, an optional matching current URL, and callable callbacks.');
}
function environmentError(reason, cause) {
    if (cause instanceof WxImagePreviewRuntimeError)
        return cause;
    return new WxImagePreviewRuntimeError('WF6230', reason, 'Run the generated application in a browser with HTMLDialogElement and image support after document.body is available.', cause);
}
function readImageUrl(value, field) {
    if (typeof value !== 'string' || value.length === 0 || value.length > MAX_URL_LENGTH) {
        throw optionError(`${field} must be a non-empty string of at most ${MAX_URL_LENGTH} characters`);
    }
    let parsed;
    try {
        parsed = new URL(value, 'https://wefat.invalid/');
    }
    catch {
        throw optionError(`${field} must be a valid image URL`);
    }
    if (!['http:', 'https:', 'blob:', 'data:'].includes(parsed.protocol)) {
        throw optionError(`${field} uses unsupported protocol ${parsed.protocol}`);
    }
    if (parsed.protocol === 'data:' && !value.slice(0, 11).toLowerCase().startsWith('data:image/')) {
        throw optionError(`${field} data URL must contain an image media type`);
    }
    return value;
}
function readPresentation(raw) {
    if (!isPlainRecord(raw))
        throw optionError('options must be a plain object');
    for (const key of Reflect.ownKeys(raw)) {
        const descriptor = typeof key === 'string' ? Object.getOwnPropertyDescriptor(raw, key) : undefined;
        if (typeof key !== 'string' || !OPTION_KEYS.has(key)) {
            throw optionError(`unsupported option field ${String(key)}`);
        }
        if (descriptor === undefined || !('value' in descriptor)) {
            throw optionError(`option field ${key} must be a direct data property`);
        }
        if (CALLBACK_KEYS.has(key) && descriptor.value !== undefined && typeof descriptor.value !== 'function') {
            throw optionError(`${key} must be a function when provided`);
        }
    }
    if (!Array.isArray(raw.urls) || raw.urls.length === 0 || raw.urls.length > MAX_IMAGE_COUNT) {
        throw optionError(`urls must contain from 1 through ${MAX_IMAGE_COUNT} images`);
    }
    const urls = [];
    for (let index = 0; index < raw.urls.length; index += 1) {
        const descriptor = Object.getOwnPropertyDescriptor(raw.urls, String(index));
        if (descriptor === undefined || !('value' in descriptor)) {
            throw optionError(`urls[${index}] must be a direct data property`);
        }
        urls.push(readImageUrl(descriptor.value, `urls[${index}]`));
    }
    const firstUrl = urls[0];
    if (firstUrl === undefined)
        throw optionError('urls must contain at least one image');
    const current = raw.current === undefined ? firstUrl : readImageUrl(raw.current, 'current');
    const currentIndex = urls.indexOf(current);
    if (currentIndex < 0)
        throw optionError('current must match one entry in urls');
    return {
        callbacks: raw,
        presentation: Object.freeze({ currentIndex, urls: Object.freeze(urls) }),
    };
}
function invokeCallback(callback, value, kind, report) {
    if (callback === undefined)
        return;
    try {
        callback(value);
    }
    catch (error) {
        if (report !== undefined) {
            try {
                report(error, 'previewImage', kind);
                return;
            }
            catch (reportError) {
                queueMicrotask(() => { throw reportError; });
                return;
            }
        }
        queueMicrotask(() => { throw error; });
    }
}
function browserPreviewImage(presentation) {
    if (typeof HTMLDialogElement === 'undefined' || typeof HTMLDialogElement.prototype.showModal !== 'function') {
        return Promise.reject(environmentError('HTML dialog support is unavailable'));
    }
    const host = document.querySelector('[data-wefat-app]') ?? document.body;
    if (host === null)
        return Promise.reject(environmentError('document.body is unavailable'));
    activeViewers.get(document)?.();
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.documentElement.style.overflow;
    const dialog = document.createElement('dialog');
    dialog.className = 'wefat-image-preview';
    dialog.setAttribute('data-wefat-feedback', 'previewImage');
    dialog.setAttribute('aria-label', 'Image preview');
    const close = document.createElement('button');
    close.autofocus = true;
    close.className = 'wefat-image-preview__close';
    close.type = 'button';
    close.setAttribute('aria-label', 'Close image preview');
    close.title = 'Close image preview';
    close.textContent = '\u00d7';
    const stage = document.createElement('div');
    stage.className = 'wefat-image-preview__stage';
    const status = document.createElement('p');
    status.className = 'wefat-image-preview__status';
    status.setAttribute('aria-live', 'polite');
    const count = document.createElement('p');
    count.className = 'wefat-image-preview__count';
    const previous = document.createElement('button');
    previous.className = 'wefat-image-preview__previous';
    previous.type = 'button';
    previous.setAttribute('aria-label', 'Previous image');
    previous.title = 'Previous image';
    previous.textContent = '\u2039';
    const next = document.createElement('button');
    next.className = 'wefat-image-preview__next';
    next.type = 'button';
    next.setAttribute('aria-label', 'Next image');
    next.title = 'Next image';
    next.textContent = '\u203a';
    dialog.append(close, stage, status, count, previous, next);
    let currentIndex = presentation.currentIndex;
    let currentImage = null;
    let imageVersion = 0;
    let closed = false;
    const cleanup = () => {
        if (closed)
            return;
        closed = true;
        if (dialog.open)
            dialog.close();
        dialog.remove();
        document.documentElement.style.overflow = previousOverflow;
        if (activeViewers.get(document) === cleanup)
            activeViewers.delete(document);
        previousFocus?.focus();
    };
    const renderImage = () => {
        const source = presentation.urls[currentIndex];
        if (source === undefined)
            return;
        imageVersion += 1;
        const version = imageVersion;
        currentImage?.remove();
        const image = document.createElement('img');
        currentImage = image;
        image.className = 'wefat-image-preview__image';
        image.alt = `Preview image ${currentIndex + 1} of ${presentation.urls.length}`;
        image.addEventListener('load', () => {
            if (version !== imageVersion)
                return;
            status.textContent = '';
            dialog.setAttribute('data-wefat-image-state', 'ready');
        }, { once: true });
        image.addEventListener('error', () => {
            if (version !== imageVersion)
                return;
            status.textContent = 'Image unavailable';
            dialog.setAttribute('data-wefat-image-state', 'error');
        }, { once: true });
        status.textContent = 'Loading image';
        count.textContent = `${currentIndex + 1} / ${presentation.urls.length}`;
        previous.disabled = currentIndex === 0;
        next.disabled = currentIndex === presentation.urls.length - 1;
        dialog.setAttribute('data-wefat-image-state', 'loading');
        stage.appendChild(image);
        image.src = source;
    };
    const move = (offset) => {
        const target = currentIndex + offset;
        if (target < 0 || target >= presentation.urls.length)
            return;
        currentIndex = target;
        renderImage();
    };
    close.addEventListener('click', cleanup);
    previous.addEventListener('click', () => move(-1));
    next.addEventListener('click', () => move(1));
    dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        cleanup();
    });
    dialog.addEventListener('click', (event) => {
        if (event.target === dialog)
            cleanup();
    });
    dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            move(-1);
        }
        else if (event.key === 'ArrowRight') {
            event.preventDefault();
            move(1);
        }
    });
    try {
        host.appendChild(dialog);
        renderImage();
        dialog.showModal();
        document.documentElement.style.overflow = 'hidden';
        activeViewers.set(document, cleanup);
        return Promise.resolve();
    }
    catch (error) {
        cleanup();
        return Promise.reject(environmentError('could not display the browser image preview', error));
    }
}
function browserEnvironment() {
    if (typeof document === 'undefined')
        return null;
    return { previewImage: browserPreviewImage };
}
export function createBrowserImagePreviewWxApis(options = {}) {
    if (options.onCallbackError !== undefined && typeof options.onCallbackError !== 'function') {
        throw new TypeError('onCallbackError must be a function when provided');
    }
    const environment = options.environment ?? browserEnvironment();
    if (environment === null || !isPlainRecord(environment) || typeof environment.previewImage !== 'function') {
        throw new TypeError('createBrowserImagePreviewWxApis requires a browser document or injected environment');
    }
    return Object.freeze({
        previewImage(raw) {
            const { callbacks, presentation } = readPresentation(raw);
            const task = Promise.resolve()
                .then(() => environment.previewImage(presentation))
                .then(() => Object.freeze({ errMsg: 'previewImage:ok' }))
                .catch((error) => {
                throw environmentError(error instanceof Error ? error.message : 'browser image preview failed', error);
            });
            if (callbacks.success === undefined && callbacks.fail === undefined && callbacks.complete === undefined) {
                return task;
            }
            void task.then((result) => {
                invokeCallback(callbacks.success, result, 'success', options.onCallbackError);
                invokeCallback(callbacks.complete, result, 'complete', options.onCallbackError);
            }, (error) => {
                invokeCallback(callbacks.fail, error, 'fail', options.onCallbackError);
                invokeCallback(callbacks.complete, error, 'complete', options.onCallbackError);
            });
            return undefined;
        },
    });
}
//# sourceMappingURL=image-preview-adapter.js.map